import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { axiosCompany, displayDate, mapStateToProps, SpinnerButton } from '../../helpers/Common'
import TrackerLogo from '../../assets/images/kerjoo-tracker-logo.png'
import TrackerLogoDark from '../../assets/images/kerjoo-tracker-logo-dark.png'
import { isLangEN, LangResetDataFirst } from '../../constant/languange'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCamera, faChartBar, faCheckCircle, faClock, faDesktop, faExternalLinkAlt, faInfoCircle, faStopwatch } from '@fortawesome/free-solid-svg-icons'

/** Buka Kerjoo Tracker lewat SSO (token sekali pakai), dipakai juga oleh app switcher di sidebar. */
export function openKerjooTracker () {
  // Tab dibuka lebih dulu agar tidak diblokir popup blocker (window.open setelah request async)
  const tab = window.open('', '_blank')
  return axiosCompany().get('/integrations/tracker/sso-token')
    .then((res) => {
      const { ott_key, tracker_app_url } = res.data
      const url = `${tracker_app_url}/auth/callback?ott=${encodeURIComponent(ott_key)}`
      if (tab) tab.location.href = url
      else window.location.href = url
    })
    .catch((err) => {
      if (tab) tab.close()
      throw err
    })
}

// tombol yang diblokir saat data contoh: benar-benar tidak bisa diklik/hover
const blockedStyle = { pointerEvents: 'none', cursor: 'not-allowed', opacity: 0.5 }

const palette = (dark) => ({
  heroBg: dark ? 'linear-gradient(135deg, #1f2b22 0%, #232b29 100%)' : 'linear-gradient(135deg, #f0fbef 0%, #f5fbf0 100%)',
  heroBorder: dark ? '#30483a' : '#d6f0d3',
  card: dark ? '#262733' : '#ffffff',
  cardBorder: dark ? '#34354a' : '#ecebf5',
  text: dark ? '#f3f4f6' : '#111827',
  muted: dark ? '#a1a1b5' : '#6b7280',
  accent: dark ? '#7fd178' : '#3d9a37',
  accentSoft: dark ? 'rgba(127, 209, 120, 0.16)' : '#e2f5df',
  success: dark ? '#4ade80' : '#15803d',
  successSoft: dark ? 'rgba(74, 222, 128, 0.14)' : '#dcfce7',
  danger: dark ? '#f87171' : '#dc2626'
})

/** Ilustrasi aktivitas tim di Kerjoo Tracker (contoh, bukan data asli). */
function ActivityPreview ({ c }) {
  const rows = [
    { name: 'Rina', app: 'Figma', time: '6j 12m', pct: 86, who: 'R' },
    { name: 'Andi', app: 'VS Code', time: '5j 40m', pct: 74, who: 'A' },
    { name: 'Dewi', app: 'Google Sheets', time: '4j 05m', pct: 58, who: 'D' }
  ]

  return (
    <div aria-hidden style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, boxShadow: '0 12px 32px rgba(61, 154, 55, 0.12)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${c.cardBorder}` }}>
        <FontAwesomeIcon icon={faChartBar} style={{ color: c.accent }} />
        <span style={{ fontWeight: 600, fontSize: 13, color: c.text }}>{isLangEN ? 'Today' : 'Hari ini'}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: c.accent, background: c.accentSoft, borderRadius: 999, padding: '2px 8px' }}>
          {isLangEN ? '3 active' : '3 aktif'}
        </span>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < rows.length - 1 ? `1px solid ${c.cardBorder}` : 'none' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: c.accentSoft, color: c.accent, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.who}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: c.text }}>{r.name} <span style={{ fontWeight: 400, color: c.muted }}>· {r.app}</span></span>
              <span style={{ color: c.muted, fontSize: 12 }}>{r.time}</span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: c.accentSoft, overflow: 'hidden' }}>
              <div style={{ width: `${r.pct}%`, height: '100%', background: c.accent, borderRadius: 999 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function DeveloperKerjooTrackerComponent ({ redux }) {
  const dark = !!redux.darkModeOn
  const c = palette(dark)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingSso, setLoadingSso] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    axiosCompany().get('/integrations/tracker/status')
      .then((res) => setStatus(res.data))
      .catch(() => setStatus({ is_connected: false, linked: null }))
  }, [])

  const handleConnect = () => {
    if (!canConnect) return
    setLoading(true)
    setError('')
    axiosCompany().post('/integrations/tracker/connect')
      .then(() => {
        window.location.hash = '#/developer?tab=kerjoo-tracker'
        window.location.reload()
      })
      .catch((err) => {
        setError(err.response?.data?.message || (isLangEN ? 'Connection failed' : 'Koneksi gagal'))
        setLoading(false)
      })
  }

  const handleSso = () => {
    setLoadingSso(true)
    setError('')
    openKerjooTracker()
      .catch((err) => setError(err.response?.data?.message || (isLangEN ? 'Failed to open Kerjoo Tracker' : 'Gagal membuka Kerjoo Tracker')))
      .finally(() => setLoadingSso(false))
  }

  const features = [
    {
      icon: faStopwatch,
      title: isLangEN ? 'Real working time' : 'Waktu kerja sebenarnya',
      text: isLangEN ? 'Active and idle time per employee, recorded automatically from the desktop app.' : 'Waktu aktif dan idle per pegawai, tercatat otomatis dari aplikasi desktop.'
    },
    {
      icon: faDesktop,
      title: isLangEN ? 'Apps & websites' : 'Aplikasi & situs',
      text: isLangEN ? 'See which apps and websites are used, grouped by productivity category.' : 'Lihat aplikasi dan situs yang dipakai, dikelompokkan per kategori produktivitas.'
    },
    {
      icon: faCamera,
      title: isLangEN ? 'Periodic screenshots' : 'Screenshot berkala',
      text: isLangEN ? 'Screen captures at an interval you set, per company or per employee.' : 'Tangkapan layar dengan interval yang Anda atur, per perusahaan atau per pegawai.'
    },
    {
      icon: faClock,
      title: isLangEN ? 'Connected to Kerjoo' : 'Terhubung dengan Kerjoo',
      text: isLangEN ? 'Employees, schedules, and tasks come straight from Kerjoo, no double entry.' : 'Pegawai, jadwal, dan tugas langsung dari Kerjoo, tanpa input ulang.'
    }
  ]

  const steps = [
    {
      title: isLangEN ? 'Connect' : 'Hubungkan',
      text: isLangEN ? 'Click Start Connection, then open Kerjoo Tracker from here.' : 'Klik Mulai Koneksi, lalu buka Kerjoo Tracker dari sini.'
    },
    {
      title: isLangEN ? 'Sync employees' : 'Sinkronkan pegawai',
      text: isLangEN ? 'In Kerjoo Tracker, sync employees and choose who is tracked.' : 'Di Kerjoo Tracker, sinkronkan pegawai dan pilih siapa yang di-tracking.'
    },
    {
      title: isLangEN ? 'Install the desktop app' : 'Pasang aplikasi desktop',
      text: isLangEN ? 'Employees log in with their Kerjoo email and password.' : 'Pegawai login dengan email dan password Kerjoo mereka.'
    }
  ]

  const linked = status?.linked
  // koneksi baru diizinkan setelah data contoh (trial) dikosongkan
  const isTemporary = !!redux.isTemporary
  const canConnect = !isTemporary

  const renderAction = () => {
    if (!status) return <div style={{ padding: '12px 0' }}>{SpinnerButton}</div>

    if (status.is_connected) {
      return (
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: c.successSoft, color: c.success, padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            {isLangEN
              ? `Connected since ${displayDate(status.connected_at, 'DD MMMM YYYY')}`
              : `Terhubung sejak ${displayDate(status.connected_at, 'DD MMMM YYYY')}`}
          </div>
          <div>
            <button disabled={loadingSso} onClick={handleSso} className='btn btn-primary' style={{ borderRadius: 10, padding: '11px 24px', fontWeight: 600 }}>
              {loadingSso && SpinnerButton} {isLangEN ? 'Open Kerjoo Tracker' : 'Buka Kerjoo Tracker'} <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: 6, fontSize: 12 }} />
            </button>
          </div>
          <p style={{ color: c.muted, fontSize: 13, margin: '14px 0 0', lineHeight: 1.6 }}>
            {isLangEN
              ? 'Kerjoo admins and sub-admins can open Kerjoo Tracker from here without logging in again.'
              : 'Admin dan sub-admin Kerjoo bisa membuka Kerjoo Tracker dari sini tanpa login ulang.'}
          </p>
        </div>
      )
    }

    return (
      <div>
        {linked && (
          <p style={{ color: c.muted, marginBottom: 16, lineHeight: 1.6 }}>
            {isLangEN
              ? 'This company was connected to Kerjoo Tracker before. Your tracking data there is still intact.'
              : 'Perusahaan ini pernah terhubung ke Kerjoo Tracker. Data tracking Anda di sana masih utuh.'}
          </p>
        )}
        <button disabled={loading || !canConnect} onClick={handleConnect} className='btn btn-primary' style={{ borderRadius: 10, padding: '11px 24px', fontWeight: 600, ...(isTemporary && blockedStyle) }}>
          {loading && SpinnerButton} {linked ? (isLangEN ? 'Reconnect' : 'Sambungkan Kembali') : (isLangEN ? 'Start Connection' : 'Mulai Koneksi')}
          {!loading && <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8, fontSize: 12 }} />}
        </button>
        {isTemporary && (
          <p style={{ color: c.muted, fontSize: 13, margin: '12px 0 0' }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 6 }} />{LangResetDataFirst}
          </p>
        )}
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ background: c.heroBg, border: `1px solid ${c.heroBorder}`, borderRadius: 18, padding: '32px 28px', marginBottom: 24 }}>
        <div className='row align-items-center'>
          <div className='col-lg-7 mb-4 mb-lg-0'>
            <img src={dark ? TrackerLogoDark : TrackerLogo} alt='Kerjoo Tracker' style={{ height: 34, marginBottom: 20 }} />
            <h4 style={{ fontWeight: 800, fontSize: 26, lineHeight: 1.3, color: c.text, marginBottom: 10 }}>
              {isLangEN ? 'Know how your team actually works' : 'Ketahui cara tim Anda bekerja'}
            </h4>
            <p style={{ color: c.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 24, maxWidth: 520 }}>
              {isLangEN
                ? 'Working time, apps, and screenshots from your employees\' computers. Connect once, then open Kerjoo Tracker straight from Kerjoo without logging in again.'
                : 'Waktu kerja, aplikasi, dan screenshot dari komputer pegawai. Hubungkan sekali, lalu buka Kerjoo Tracker langsung dari Kerjoo tanpa login ulang.'}
            </p>
            {renderAction()}
            {error && <p style={{ color: c.danger, margin: '12px 0 0', fontSize: 14 }}>{error}</p>}
          </div>
          <div className='col-lg-5'>
            <ActivityPreview c={c} />
          </div>
        </div>
      </div>

      <div style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, padding: '24px 28px 8px' }}>
        <div className='row'>
          {features.map((f, i) => (
            <div key={i} className='col-md-6 mb-4'>
              <div className='d-flex align-items-center' style={{ gap: 10, marginBottom: 6 }}>
                <FontAwesomeIcon icon={f.icon} style={{ color: c.accent, fontSize: 14, width: 16 }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: c.text }}>{f.title}</span>
              </div>
              <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.6, paddingLeft: 26 }}>{f.text}</div>
            </div>
          ))}
        </div>
      </div>

      {status && !status.is_connected && !linked && (
        <div style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, padding: '20px 28px', marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: c.text, marginBottom: 16 }}>{isLangEN ? 'How to get started' : 'Cara memulai'}</div>
          <div className='row'>
            {steps.map((s, i) => (
              <div key={i} className='col-md-4 mb-3 mb-md-0'>
                <div className='d-flex align-items-start' style={{ gap: 12 }}>
                  <span style={{ width: 28, height: 28, borderRadius: '50%', background: c.accent, color: '#fff', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: c.text, marginBottom: 4 }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.6 }}>{s.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps)(DeveloperKerjooTrackerComponent)
